import React, { useEffect, useRef, useState } from "react";
import api from "../../../../services/api";
import { renderError } from "../../../../helpers/errors";
import moment from "moment";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import Icon from "../../../../components/Icon";
import { getUser } from "../../../../services/auth";
import { ModalDelete } from "../../../../components/ModalDelete";
import { Link } from "react-router-dom";
import { getCaretCoordinates } from "../../../../helpers/caret";
import Avatar from "../../../../components/Avatar";

const formatDayLabel = date => {
    const day = moment(date);
    if(day.isSame(moment(), 'day')) return 'Hoje';
    if(day.isSame(moment().subtract(1, 'day'), 'day')) return 'Ontem';
    return day.format('DD/MM/YYYY');
};

export const CRMTimeline = ({cardId}) => {
    const [activities, setActivities] = useState([]);
    const [gettingActivities, setGettingActivities] = useState(true);

    const [activityToEdit, setActivityToEdit] = useState(null);
    const [updatingActivity, setUpdatingActivity] = useState(false);
    const [activityIdToDelete, setActivityIdToDelete] = useState(null);

    const [adminList, setAdminList] = useState([]);
    const [mentionActive, setMentionActive] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionCursorStart, setMentionCursorStart] = useState(0);
    const [mentionIndex, setMentionIndex] = useState(0);
    const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 });
    const textareaRef = useRef(null);

    const user = getUser();

    const normalize = s => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

    const mentionFiltered = adminList
        .filter(u => u.id !== user?.id)
        .filter(u => normalize(u.name).includes(normalize(mentionSearch)))
        .slice(0, 8);

    useEffect(() => {
        if(cardId){
            getActivities();
        }
    }, [cardId]);

    useEffect(() => {
        api.get('/list').then(res => setAdminList(res.data.resources)).catch(() => {});
    }, []);

    const getActivities = () => {
        setGettingActivities(true);

        api.get(`/pipeline-stage-card-activity?pipeline_stage_card_id=${cardId}`).then(res => {
            setActivities(res.data.resources);
        }).catch(error => {
            renderError(error);
        }).then(() => setGettingActivities(false));
    }

    const createActivity = (type) => {
        setActivityToEdit({
            id: null,
            pipeline_stage_card_id: cardId,
            type: type,
            content: '',
            admin_id: user.id,
            admin_name: user.name,
            admin_email: user.email
        });
    }

    const updateActivity = () => {
        setUpdatingActivity(true);

        const payload = {
            ...activityToEdit,
            mentioned_admin_ids: extractMentionedAdminIds(activityToEdit.content)
        };

        api[activityToEdit.id ? 'put' : 'post'](`/pipeline-stage-card-activity/${activityToEdit.id ?? ''}`, payload).then(res => {
            setActivityToEdit(null);
            getActivities();
        }).catch(error => {
            renderError(error);
        }).then(() => setUpdatingActivity(false));
    }

    const deleteActivity = () => {
        setUpdatingActivity(true);

        api.delete(`/pipeline-stage-card-activity/${activityIdToDelete}`).then(res => {
            setActivityIdToDelete(null);
            setActivityToEdit(null);
            getActivities();
        }).catch(error => {
            renderError(error)
        }).then(() => setUpdatingActivity(false));
    }

    const handleContentChange = (e) => {
        const value = e.target.value;
        const cursor = e.target.selectionStart;
        const textBeforeCursor = value.slice(0, cursor);
        const match = textBeforeCursor.match(/@([^@\s]*)$/);
        if (match) {
            setMentionActive(true);
            setMentionSearch(match[1]);
            setMentionCursorStart(cursor - match[0].length);
            setMentionIndex(0);

            const textarea = e.target;
            const caret = getCaretCoordinates(textarea, cursor);
            setMentionPos({
                top: caret.top - textarea.scrollTop + caret.height,
                left: caret.left - textarea.scrollLeft
            });
        } else {
            setMentionActive(false);
        }
        setActivityToEdit(prev => ({...prev, content: value}));
    };

    const extractMentionedAdminIds = (content) => {
        const names = [...(content || '').matchAll(/@\[([^\]]+)\]/g)].map(m => m[1]);
        const ids = names
            .map(name => adminList.find(a => a.name === name)?.id)
            .filter(Boolean);
        return [...new Set(ids)];
    };

    const insertMention = (admin) => {
        const content = activityToEdit.content;
        const cursor = textareaRef.current.selectionStart;
        const before = content.slice(0, mentionCursorStart);
        const after = content.slice(cursor);
        setActivityToEdit(prev => ({...prev, content: `${before}@[${admin.name}] ${after}`}));
        setMentionActive(false);
        setMentionSearch('');
        setTimeout(() => textareaRef.current?.focus(), 0);
    };

    const renderContent = (text) => {
        if (!text) return null;
        const parts = text.split(/(@\[[^\]]+\])/g);
        return parts.map((part, i) => {
            const match = part.match(/^@\[([^\]]+)\]$/);
            if (match) {
                return <span key={i} className="crm-mention-highlight">@{match[1]}</span>;
            }
            return part;
        });
    };

    const handleKeyDown = (e) => {
        if (!mentionActive || mentionFiltered.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setMentionIndex(i => Math.min(i + 1, mentionFiltered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setMentionIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            insertMention(mentionFiltered[mentionIndex]);
        } else if (e.key === 'Escape') {
            setMentionActive(false);
        }
    };

    const sortedActivities = [...activities].sort((a, b) =>
        moment(b.created_at) - moment(a.created_at)
    );

    const dayGroups = sortedActivities.reduce((groups, activity) => {
        const key = moment(activity.created_at).format('YYYY-MM-DD');
        const group = groups.find(g => g.key === key);
        if(group) group.items.push(activity);
        else groups.push({key, label: formatDayLabel(activity.created_at), items: [activity]});
        return groups;
    }, []);

    const commentsCount = activities.filter(a => a.type === 'comments').length;
    const budgetsCount = activities.filter(a => a.type === 'budget').length;
    const ordersCount = activities.filter(a => a.type === 'order').length;

    const renderActivity = activity => (
        <div key={activity.id} className={`activity activity-${activity.type}`}>
            <span className="node">
                <Icon name={`activity-${activity.type}`} size={15} />
            </span>
            <div className="card">
                <div className="header">
                    <div className="data">
                        {activity.type === 'comments' &&
                            <span className="desc">
                                <Avatar name={activity.admin_name} photo={activity.admin_photo_url} size={22} />
                                <span><b>{activity.admin_name}</b> escreveu uma observação</span>
                            </span>
                        }
                        {activity.type === 'budget' &&
                            <span className="desc">
                                <span>Orçamento <Link className="resource-link" to={`/quote/edit/${activity.budget_id}`}>{activity.budget_code}</Link> cadastrado</span>
                            </span>
                        }
                        {activity.type === 'order' &&
                            <span className="desc">
                                <span>Pedido <Link className="resource-link" to={`/order/edit/${activity.order_id}`}>{activity.order_code}</Link> cadastrado</span>
                            </span>
                        }
                        <span className="date">{moment(activity.created_at).format('HH:mm')}</span>
                    </div>
                    {activity.type === 'comments' &&
                        <div className="actions">
                            <button
                                className="action action-edit"
                                title={`Editar`}
                                onClick={() => setActivityToEdit(activity)}
                            >
                                <Icon name={`edit`} size={16} />
                            </button>
                            <button
                                className="action action-delete"
                                title={`Excluir`}
                                onClick={() => setActivityIdToDelete(activity.id)}
                            >
                                <Icon name={`delete`} size={16} />
                            </button>
                        </div>
                    }
                </div>
                {activity.content &&
                    <div className="content">
                        <p>{renderContent(activity.content)}</p>
                    </div>
                }
            </div>
        </div>
    );

    return (
        <>

            {activityToEdit?.type === 'comments' &&
                <Modal
                    show={true}
                    title={`${activityToEdit.id ? 'Editar' : 'Criar'} observação`}
                    close={() => setActivityToEdit(null)}
                    size={`small`}
                    footer={
                        <>
                            {activityToEdit.id &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`delete-small`}
                                    text={`Excluir`}
                                    action={() => setActivityIdToDelete(activityToEdit.id)}
                                    loading={updatingActivity}
                                />
                            }
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={`Salvar alterações`}
                                svg={`save-white`}
                                action={updateActivity}
                                loading={updatingActivity}
                            />
                        </>
                    }
                >
                    <div className="section">
                        <div className="crm-mention-wrapper">
                            <textarea
                                ref={textareaRef}
                                className="crm-note-textarea"
                                rows={10}
                                value={activityToEdit.content}
                                onChange={handleContentChange}
                                onKeyDown={handleKeyDown}
                                onBlur={() => setMentionActive(false)}
                                autoFocus={true}
                                placeholder="Escreva a observação... Digite @ para mencionar um usuário."
                            />
                            {mentionActive && mentionFiltered.length > 0 &&
                                <div
                                    className="crm-mention-dropdown"
                                    style={{ top: mentionPos.top, left: mentionPos.left }}
                                >
                                    {mentionFiltered.map((admin, i) => (
                                        <div
                                            key={admin.id}
                                            className={`crm-mention-item${i === mentionIndex ? ' active' : ''}`}
                                            onMouseDown={e => { e.preventDefault(); insertMention(admin); }}
                                        >
                                            <Avatar name={admin.name} photo={admin.photo_url} size={26} />
                                            <div className="crm-mention-item__text">
                                                <strong>{admin.name}</strong>
                                                <span>{admin.role_name}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            }
                        </div>
                    </div>
                </Modal>
            }
            <ModalDelete
                show={activityIdToDelete !== null}
                close={() => setActivityIdToDelete(null)}
                action={deleteActivity}
                loading={updatingActivity}
            />
            <div className="timeline">
                <div className="toolbar">
                    <div className="summary">
                        {!gettingActivities && activities.length > 0 &&
                            <>
                                {commentsCount > 0 &&
                                    <span className="pill">
                                        <Icon name={`activity-comments`} size={13} />
                                        {commentsCount} observaç{commentsCount === 1 ? 'ão' : 'ões'}
                                    </span>
                                }
                                {budgetsCount > 0 &&
                                    <span className="pill">
                                        <Icon name={`activity-budget`} size={13} />
                                        {budgetsCount} orçamento{budgetsCount === 1 ? '' : 's'}
                                    </span>
                                }
                                {ordersCount > 0 &&
                                    <span className="pill">
                                        <Icon name={`activity-order`} size={13} />
                                        {ordersCount} pedido{ordersCount === 1 ? '' : 's'}
                                    </span>
                                }
                            </>
                        }
                    </div>
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`plus-white`}
                        text={`Nova observação`}
                        action={() => createActivity('comments')}
                    />
                </div>
                <div className="activities">
                    {gettingActivities &&
                        <>
                            {[0, 1, 2].map(i => (
                                <div key={i} className="activity-skeleton">
                                    <span className="sk sk-node" />
                                    <div className="sk-lines">
                                        <span className="sk sk-line" />
                                        <span className="sk sk-line short" />
                                    </div>
                                </div>
                            ))}
                        </>
                    }
                    {!gettingActivities && activities.length === 0 &&
                        <div className="empty-state">
                            <span className="empty-icon">
                                <Icon name={`activity-comments`} size={26} />
                            </span>
                            <strong>Nenhuma atividade ainda</strong>
                            <p>Registre observações sobre as conversas com o cliente — orçamentos e pedidos vinculados também aparecem aqui automaticamente.</p>
                            <Button
                                type={`secondary`}
                                size={`small`}
                                svg={`plus-blue`}
                                text={`Criar primeira observação`}
                                action={() => createActivity('comments')}
                            />
                        </div>
                    }
                    {!gettingActivities && dayGroups.map(group => (
                        <div key={group.key} className="day-group">
                            <div className="day-label">
                                <span>{group.label}</span>
                            </div>
                            {group.items.map(renderActivity)}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

export default CRMTimeline;
